import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import type { AiAccount } from './ai-matches.service';

/**
 * A provider was saved (its id). The server places its blocks again on save (#248; plan §4.4), so anything showing
 * where they stand reloads. Module-level: provider saves happen in ServiceProvider, which isn't injected.
 */
export const providerSaved$ = new Subject<number>();

export type BlockStatus = 'suggested' | 'no-match' | 'unchecked' | 'linked' | 'pinned' | 'duplicate';

export interface CrawlPlace {
  locationId: number;
  name: string | null;
  kind: string;
  alsoKinds: string[];
  lat: number;
  lng: number;
  phones: string[];
  website: string | null;
  street: string | null;
  suburb: string | null;
  source: string;
  gone?: boolean;
}

export interface Suggestion extends CrawlPlace {
  score: number;
  /** The rules' signals; an AI suggestion may carry none (its account is in ai). */
  reasons: { name: number; phone: boolean; street: boolean; kind: boolean; distanceM: number | null } | null;
  /** Who suggested it: the rules, the AI, or a person who sent an automatic link back (#261). */
  decidedBy?: 'user' | 'rule' | 'ai';
  ai?: AiAccount | null;
}

export interface Usage {
  count12m: number;
  countAll: number;
  lastUsedAt: string | null;
}

export interface NearbyProvider {
  serviceId: number;
  serviceMetaId: string;
  blockType: string;
  name: string;
  phone: string | null;
  address: LocatedBlock['address'];
  lat: number;
  lng: number;
  distanceM: number;
  positionSource: 'crawl' | 'gnaf' | 'pin';
  usage: Usage;
  duplicates: number[];
}

export interface NearbyUnplaced {
  serviceId: number;
  serviceMetaId: string;
  blockType: string;
  name: string;
  phone: string | null;
  address: LocatedBlock['address'];
  suburbDistanceM: number;
  usage: Usage;
}

export interface NearbyHandTyped extends Usage {
  locationId: number;
  name: string | null;
  kind: string;
  lat: number;
  lng: number;
  distanceM: number;
}

export interface NearbyPlace {
  /** Operations' curation: a candidate to onboard, or (only when asked to show them) hidden. */
  curation?: 'candidate' | 'hidden';
  locationId: number;
  name: string | null;
  kind: string;
  lat: number;
  lng: number;
  distanceM: number;
  website: string | null;
  phone: string | null;
}

export interface NearbyQuery {
  lat: number;
  lng: number;
  radiusKm: number;
  purpose: string;
  more?: boolean;
  groups?: string[];
}

export interface Nearby {
  centre: { lat: number; lng: number };
  radiusKm: number;
  purpose: string;
  groups: string[];
  more: boolean;
  rankedBy: '12m' | 'all-time';
  datasetLive: boolean;
  datasetGeneratedAt: string | null;
  usageComputedAt: string | null;
  providers: NearbyProvider[];
  unplaced: NearbyUnplaced[];
  handTyped: NearbyHandTyped[];
  places: NearbyPlace[];
  placesTotal: number;
  placesCapped: boolean;
}

export type CurationStatus = 'used' | 'onboarded' | 'retired' | 'candidate' | 'hidden' | 'unreviewed';

export interface Curation {
  state: 'candidate' | 'hidden';
  reason: string;
  note: string | null;
  decidedBy: number | null;
  decidedAt: string;
}

export interface CuratedPlace {
  locationId: number;
  name: string | null;
  kind: string;
  group: string;
  lat: number;
  lng: number;
  distanceM: number;
  website: string | null;
  phone: string | null;
  status: CurationStatus;
  curation: Curation | null;
  provider: { serviceId: number; name: string | null; usedCount: number } | null;
}

export interface CurationList {
  datasetLive: boolean;
  counts: Partial<Record<CurationStatus, number>>;
  total: number;
  places: CuratedPlace[];
  capped: boolean;
}

export interface PlaceCuration {
  locationId: number;
  curation: Curation | null;
  history: Array<{ id: number; action: string; reason: string | null; note: string | null; serviceId: number | null; userId: number | null; at: string }>;
}

export interface Coverage {
  datasetLive: boolean;
  groups: string[];
  regions: Array<{ id: string; name: string; slug: string; state: string | null; total: number; groups: Record<string, Record<CurationStatus, number>> }>;
  ms: number;
}

export interface DedupeMatch {
  serviceId: number;
  name: string;
  why: string[];
  hard: boolean;
}

/** On the website: a provider unless tagged Hide From Website; any other place after review (#253 step 3). */
export type Listing = 'none' | 'pending' | 'listed';

/** A crawl place in a suburb, for the Suburbs tool's map. */
export interface SuburbPlace {
  locationId: number;
  name: string;
  crawlName: string;
  kind: string;
  group: string | null;
  lat: number;
  lng: number;
  source: string;
  googlePlaceId: string | null;
  website: string | null;
  status: CurationStatus;
  provider: { serviceId: number; name: string | null; usedCount: number } | null;
  curation: Curation | null;
  listing: Listing;
  /** A kind suburb pages can show. */
  listable: boolean;
  notes: number;
}

/** A place added by hand: a typed name, a type, the pin, and optional details. */
export interface CustomPlaceInput {
  name: string;
  kind: string;
  lat: number;
  lng: number;
  street?: string | null;
  postcode?: string | null;
  website?: string | null;
  phone?: string | null;
}

export interface PlaceNote {
  id: number;
  body: string;
  createdAt: string;
  createdBy: number | null;
  firstName: string | null;
  lastName: string | null;
}

/** One place, in full: what the crawl knows, where it stands, its website listing, history and notes. */
export interface PlaceDetail {
  locationId: number;
  name: string;
  crawlName: string;
  displayName: string | null;
  kind: string | null;
  group: string | null;
  listable: boolean;
  googlePlaceId: string | null;
  position: { lat: number; lng: number } | null;
  /** osm / overture (the crawl), or custom (added by hand). */
  source: string | null;
  suburb: string | null;
  /** Added by hand (not in the crawl): its own details, editable. */
  custom: {
    salCode: string;
    street: string | null;
    postcode: string | null;
    website: string | null;
    phone: string | null;
    googlePlaceId: string | null;
    createdBy: number | null;
    createdAt: string;
  } | null;
  address: { street: string | null; suburb: string | null; postcode: string | null; state: string | null };
  phone: string | null;
  email: string | null;
  website: string | null;
  socials: Record<string, string>;
  hours: string | null;
  links: Array<{ label: string; url: string }>;
  googleMapsUrl: string | null;
  status: CurationStatus;
  provider: { serviceId: number; name: string | null; usedCount: number } | null;
  /** Arrangements naming the provider (quick check): 0 offers "Remove from Conductor". */
  providerRefs: number;
  /** The provider this place was before it was removed from Conductor: Restore brings it back. */
  removedProvider: { serviceId: number; name: string | null } | null;
  curation: Curation | null;
  listing: Listing;
  request: {
    requestedBy: number | null;
    requestedAt: string | null;
    /** Who asked: a person, or the matching pipeline's AI (#261), whose reason is the note. */
    source?: 'user' | 'ai';
    note?: string | null;
    aiModel?: string | null;
    reviewedBy: number | null;
    reviewedAt: string | null;
  } | null;
  history: Array<{ action: string; detail: string | null; at: string; firstName: string | null; lastName: string | null }>;
  notes: PlaceNote[];
}

export interface SuburbHit {
  salCode: string;
  name: string;
  state: string;
  lat: number;
  lng: number;
}

/** A place waiting for review to go on the website: asked for by a person, or suggested by the AI (#261). */
export interface PendingPlace {
  locationId: number;
  displayName: string | null;
  requestedAt: string;
  requestSource: 'user' | 'ai' | null;
  /** The AI's reason. */
  requestNote: string | null;
  aiModel: string | null;
  firstName: string | null;
  lastName: string | null;
}

/** A provider block that is a place (venue, cremation, burial, mortuary) and where it stands. */
export interface LocatedBlock {
  serviceId: number;
  serviceMetaId: string;
  blockType: 'venue' | 'cremation' | 'burial' | 'mortuary';
  name: string;
  phone: string | null;
  address: { place: string | null; street: string | null; suburb: string | null; postcode: string | null; state: string | null; international: boolean };
  addressFrom: 'block' | 'provider';
  status: BlockStatus;
  position: { lat: number; lng: number; source: 'crawl' | 'gnaf' | 'pin' } | null;
  positionStale: boolean;
  link: CrawlPlace | null;
  duplicateOf: { serviceId: number; name: string | null } | null;
  /** The provider is tagged "Do not use for new bookings". */
  retired?: boolean;
  /** Picked on the Location panel (Google's establishment Autocomplete). */
  googlePlaceId?: string | null;
  /** How the link was made: confirmed by someone, or auto (a verified place ID). */
  linkDecision?: 'confirmed' | 'auto' | null;
  /** Who made the link, and the AI's account of it (#261). */
  linkDecidedBy?: 'user' | 'rule' | 'ai' | null;
  linkAi?: AiAccount | null;
  suggestions: Suggestion[];
  suggestionsStale: boolean;
  origin: { lat: number; lng: number; salCode: string } | null;
}

/** The location layer (conductor-server #248): provider blocks linked to crawl places, reviewed by a person. */
@Injectable({ providedIn: 'root' })
export class LocationsService {
  private readonly paths = environment.api.paths.api.locations;

  constructor(private http: HttpClient) {}

  private blockUrl(b: { serviceId: number; serviceMetaId: string }, action: string): string {
    return environment.api.host + this.paths.block.replace(':serviceId', String(b.serviceId)).replace(':metaId', encodeURIComponent(b.serviceMetaId)) + '/' + action;
  }

  /** Every located block (the API pages at 100; this reads them all). */
  async allBlocks(): Promise<{ datasetId: number | null; rows: LocatedBlock[] }> {
    const rows: LocatedBlock[] = [];
    let datasetId: number | null = null;
    for (let offset = 0; ; offset += 100) {
      const page: any = (await this.http.get<any>(environment.api.host + this.paths.review, { params: { offset: String(offset), limit: '100' } }).toPromise()).data;
      datasetId = page.datasetId;
      rows.push(...page.rows);
      if (offset + 100 >= page.total) return { datasetId, rows };
    }
  }

  forProvider(serviceId: number): Observable<LocatedBlock[]> {
    return this.http.get<any>(environment.api.host + this.paths.service.replace(':serviceId', String(serviceId))).pipe(map((r) => r.data.blocks));
  }

  match(serviceId?: number): Observable<any> {
    return this.http.post<any>(environment.api.host + this.paths.match, serviceId ? { serviceId } : {}).pipe(map((r) => r.data));
  }

  confirm(b: LocatedBlock, locationId: number, clearHidden = false): Observable<LocatedBlock> {
    return this.http.post<any>(this.blockUrl(b, 'confirm'), clearHidden ? { locationId, clearHidden } : { locationId }).pipe(map((r) => r.data));
  }

  none(b: LocatedBlock): Observable<LocatedBlock> {
    return this.http.post<any>(this.blockUrl(b, 'none'), {}).pipe(map((r) => r.data));
  }

  pin(b: LocatedBlock, lat: number, lng: number): Observable<LocatedBlock> {
    return this.http.post<any>(this.blockUrl(b, 'pin'), { lat, lng }).pipe(map((r) => r.data));
  }

  unlink(b: LocatedBlock): Observable<LocatedBlock> {
    return this.http.post<any>(this.blockUrl(b, 'unlink'), {}).pipe(map((r) => r.data));
  }

  duplicate(b: LocatedBlock, duplicateOfServiceId: number | null): Observable<LocatedBlock> {
    return this.http.post<any>(this.blockUrl(b, 'duplicate'), { duplicateOfServiceId }).pipe(map((r) => r.data));
  }

  /** Start over: the block's decisions, link, pin and duplicate mark go, and it's matched again. */
  reset(b: LocatedBlock): Observable<LocatedBlock> {
    return this.http.post<any>(this.blockUrl(b, 'reset'), {}).pipe(map((r) => r.data));
  }

  /** The block's Google place ID from the Location panel's Autocomplete (only the ID is kept); null clears it. */
  setPlaceId(b: LocatedBlock, googlePlaceId: string | null, types: string[] = []): Observable<LocatedBlock> {
    return this.http.post<any>(this.blockUrl(b, 'place-id'), { googlePlaceId, types }).pipe(map((r) => r.data));
  }

  // ------------------------------------------------------------------ the arrangers' venue map (#250)

  nearby(q: NearbyQuery): Observable<Nearby> {
    const params: Record<string, string> = { lat: String(q.lat), lng: String(q.lng), radiusKm: String(q.radiusKm), purpose: q.purpose };
    if (q.more) params.more = '1';
    if (q.groups?.length) params.groups = q.groups.join(',');
    return this.http.get<any>(environment.api.host + this.paths.nearby, { params }).pipe(map((r) => r.data));
  }

  /** A suburb's centre (ABS), from its name, state and postcode only: never a street address. */
  centre(suburb: string, state: string, postcode?: string | null): Observable<{ lat: number; lng: number; salCode: string } | null> {
    const params: Record<string, string> = { suburb, state };
    if (postcode) params.postcode = postcode;
    return this.http.get<any>(environment.api.host + this.paths.centre, { params }).pipe(map((r) => r.data.centre));
  }

  suburbs(q: string): Observable<SuburbHit[]> {
    return this.http.get<any>(environment.api.host + this.paths.suburbs, { params: { q } }).pipe(map((r) => r.data.suburbs));
  }

  // ------------------------------------------------------------------ curation, coverage and onboarding (#251, #248)

  curationList(q: { lat: number; lng: number; radiusKm: number; status: string; groups: string[] }): Observable<CurationList> {
    return this.http
      .get<any>(environment.api.host + this.paths.curation, { params: { lat: String(q.lat), lng: String(q.lng), radiusKm: String(q.radiusKm), status: q.status, groups: q.groups.join(',') } })
      .pipe(map((r) => r.data));
  }

  placeCuration(locationId: number): Observable<PlaceCuration> {
    return this.http.get<any>(`${environment.api.host}${this.paths.curation}/${locationId}`).pipe(map((r) => r.data));
  }

  /** state null clears it. */
  setCuration(locationId: number, state: 'candidate' | 'hidden' | null, reason?: string, note?: string): Observable<PlaceCuration> {
    return this.http.post<any>(`${environment.api.host}${this.paths.curation}/${locationId}`, { state, reason, note }).pipe(map((r) => r.data));
  }

  coverage(): Observable<Coverage> {
    return this.http.get<any>(environment.api.host + this.paths.coverage).pipe(map((r) => r.data));
  }

  /** The dedupe gate: providers this place may already be. */
  onboardCheck(locationId: number): Observable<{ matches: DedupeMatch[] }> {
    return this.http.post<any>(environment.api.host + this.paths.onboardCheck, { locationId }).pipe(map((r) => r.data));
  }

  onboard(body: { locationId: number; blockType: string; name?: string; notDuplicateOf: number[]; clearHidden?: boolean }): Observable<{ serviceId: number; serviceMetaId: string; block: LocatedBlock }> {
    return this.http.post<any>(environment.api.host + this.paths.onboard, body).pipe(map((r) => r.data));
  }

  // ------------------------------------------------------------------ places in the Suburbs tool (#253 step 3)

  private placeUrl(locationId: number, tail = ''): string {
    return `${environment.api.host}${this.paths.base}/place/${locationId}${tail}`;
  }

  suburbPlaces(salCode: string, all = false): Observable<{ datasetLive: boolean; places: SuburbPlace[] }> {
    return this.http
      .get<any>(`${environment.api.host}${this.paths.base}/suburb/${encodeURIComponent(salCode)}/places`, { params: all ? { all: '1' } : {} })
      .pipe(map((r) => r.data));
  }

  place(locationId: number): Observable<PlaceDetail> {
    return this.http.get<any>(this.placeUrl(locationId)).pipe(map((r) => r.data));
  }

  /** Our own name for a place; null goes back to the crawl's. */
  renamePlace(locationId: number, displayName: string | null): Observable<PlaceDetail> {
    return this.http.post<any>(this.placeUrl(locationId, '/name'), { displayName }).pipe(map((r) => r.data));
  }

  /** add: on the website in one click (the person adding it is the review); request/approve/reject: the two-step review. */
  setListing(locationId: number, action: 'add' | 'request' | 'approve' | 'reject' | 'remove'): Observable<PlaceDetail> {
    return this.http.post<any>(this.placeUrl(locationId, '/listing'), { action }).pipe(map((r) => r.data));
  }

  /** remove: a provider no arrangement uses, out of Conductor; retire / unretire: "Do not use for new bookings". */
  setConductor(locationId: number, action: 'remove' | 'retire' | 'unretire' | 'restore'): Observable<PlaceDetail> {
    return this.http.post<any>(this.placeUrl(locationId, '/conductor'), { action }).pipe(map((r) => r.data));
  }

  // ------------------------------------------------------------------ places added by hand (#253 step 3)

  customKinds(): Observable<string[]> {
    return this.http.get<any>(`${environment.api.host}${this.paths.base}/custom/kinds`).pipe(map((r) => r.data.kinds));
  }

  createCustomPlace(body: CustomPlaceInput & { salCode: string }): Observable<PlaceDetail> {
    return this.http.post<any>(`${environment.api.host}${this.paths.base}/custom`, body).pipe(map((r) => r.data));
  }

  updateCustomPlace(locationId: number, body: Partial<CustomPlaceInput>): Observable<PlaceDetail> {
    return this.http.patch<any>(`${environment.api.host}${this.paths.base}/custom/${locationId}`, body).pipe(map((r) => r.data));
  }

  /** Only while it isn't on the website or in Conductor. */
  deleteCustomPlace(locationId: number): Observable<{ deleted: number }> {
    return this.http.delete<any>(`${environment.api.host}${this.paths.base}/custom/${locationId}`).pipe(map((r) => r.data));
  }

  /** The list is capped (a person's requests first, then the newest); total and bySource count every one waiting. */
  pendingPlaces(): Observable<{ places: PendingPlace[]; total?: number; bySource?: { ai: number; user: number } }> {
    return this.http.get<any>(`${environment.api.host}${this.paths.base}/places/pending`).pipe(map((r) => r.data));
  }

  addPlaceNote(locationId: number, body: string): Observable<PlaceNote[]> {
    return this.http.post<any>(this.placeUrl(locationId, '/notes'), { body }).pipe(map((r) => r.data));
  }

  deletePlaceNote(locationId: number, id: number): Observable<PlaceNote[]> {
    return this.http.delete<any>(this.placeUrl(locationId, `/notes/${id}`)).pipe(map((r) => r.data));
  }

  search(b: LocatedBlock, q: string): Observable<{ places: CrawlPlace[] }> {
    return this.http.get<any>(this.blockUrl(b, 'search'), { params: { q } }).pipe(map((r) => r.data));
  }
}
